import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { Delivery, NotificationPreferences, Webhook } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head, Measure } from "@/ui/frame/Page";
import { Button } from "@/ui/Button";
import { OrgSettingsScreens } from "@/features/org/screens";
import { Checkbox, Field, Input } from "@/ui/Field";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Dialog } from "@/ui/Overlay";
import { CopyField } from "@/ui/Copy";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/o/$orgId/notifications")({
  component: NotificationsScreen,
});

type Tone = ChipTone | undefined;

/**
 * What a delivery's state means to whoever is reading the list.
 *
 * A pending delivery that has already failed a few times is not "sending" in
 * any sense the reader would recognise — the endpoint has said no several
 * times and we are waiting to try again. Saying "retrying" next to the reason
 * it failed is the difference between a coherent row and a contradictory one.
 */
function describeDelivery(delivery: Delivery): { label: string; tone: Tone } {
  switch (delivery.status) {
    case "pending":
      return delivery.attempts > 1
        ? { label: "Retrying", tone: "warn" }
        : { label: "Sending", tone: "live" };
    case "delivered":
      return { label: "Delivered", tone: undefined };
    case "failed":
      return { label: "Failed", tone: "down" };
    default:
      return { label: "Given up", tone: "warn" };
  }
}

/** Turns "service.failed" into "Service failed". */
function eventLabel(event: string): string {
  const words = event.replace(/[._]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function NotificationsScreen() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  const preferences = useQuery({
    queryKey: ["notification-preferences", orgId],
    queryFn: ({ signal }) =>
      api.get<{ preferences: NotificationPreferences }>(
        `/organisations/${orgId}/notification-preferences`,
        undefined,
        signal,
      ),
  });

  const webhooks = useQuery({
    queryKey: ["webhooks", orgId],
    queryFn: ({ signal }) =>
      api.get<{ webhooks: Webhook[] | null }>(`/organisations/${orgId}/webhooks`, undefined, signal),
  });

  const deliveries = useQuery({
    queryKey: ["deliveries", orgId],
    queryFn: ({ signal }) =>
      api.get<{ deliveries: Delivery[] | null }>(
        `/organisations/${orgId}/deliveries`,
        { limit: 50 },
        signal,
      ),
  });

  const setMuted = useMutation({
    mutationFn: (muted: string[]) =>
      api.put<{ preferences: NotificationPreferences }>(
        `/organisations/${orgId}/notification-preferences`,
        { muted },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["notification-preferences", orgId] });
    },
    onError: toast.error,
  });

  const removeWebhook = useMutation({
    mutationFn: (webhook: Webhook) =>
      api.delete<void>(`/organisations/${orgId}/webhooks/${webhook.id}`),
    onSuccess: async () => {
      toast.success("Webhook removed");
      await queryClient.invalidateQueries({ queryKey: ["webhooks", orgId] });
    },
    onError: toast.error,
  });

  const prefs = preferences.data?.preferences;
  const available = prefs?.available ?? [];
  const muted = new Set(prefs?.muted ?? []);

  return (
    <div className="flex flex-col gap-6">
      <Measure>
        <Head
          title="Notifications"
          description="What Drumlin tells you about, and where it sends it."
          screens={<OrgSettingsScreens />}
        />
        <div className="flex flex-col gap-8">
      {adding && (
        <AddWebhookDialog
          orgId={orgId}
          events={available}
          onClose={() => setAdding(false)}
          onCreated={(secret) => setNewSecret(secret)}
        />
      )}

      {newSecret && (
        <Dialog
          open
          onOpenChange={() => setNewSecret(null)}
          title="Your signing secret"
          description="Every delivery is signed with this. Check the signature before trusting a request. It is the only thing that proves the request came from us."
          footer={
            <Button tone="primary" onClick={() => setNewSecret(null)}>
              I have saved it
            </Button>
          }
        >
          <CopyField label="Signing secret" value={newSecret} />
          <p className="mt-3 text-[13px] text-[var(--text-secondary)]">
            This is the only time it is shown.
          </p>
        </Dialog>
      )}

      <Panel
        title="By email"
        description="Sent to everyone in this organisation. Routine successes are never emailed: only things worth acting on."
      >
        {preferences.isLoading ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : preferences.isError ? (
          <ErrorState
            error={preferences.error}
            compact
            onRetry={() => void preferences.refetch()}
          />
        ) : available.length === 0 ? (
          <p className="text-[13px] text-[var(--text-secondary)]">
            There is nothing to configure yet.
          </p>
        ) : (
          /* Checkboxes, because these are choices.
             They were rows with a ghost button on the right reading
             "Emailed" — which is a word, in body colour, with no border: it
             reads as the current state and nothing about it says it can be
             pressed. Six of them stacked made a column of the same word six
             times, and the only way to stop an email looked like a label.
             A ticked box is unambiguously a thing you can untick, and it
             says which way round the setting is without a second word. */
          <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
            {available.map((event) => {
              const isMuted = muted.has(event);
              return (
                <li key={event}>
                  <Checkbox
                    checked={!isMuted}
                    disabled={setMuted.isPending}
                    label={eventLabel(event)}
                    onChange={() => {
                      const next = new Set(muted);
                      if (isMuted) next.delete(event);
                      else next.add(event);
                      setMuted.mutate([...next]);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel
        title="Webhooks"
        description="The same events, sent to a URL of yours."
        actions={
          <Button tone="secondary" size="dense" onClick={() => setAdding(true)}>
            Add webhook
          </Button>
        }
        padded={false}
      >
        {webhooks.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={2} columns={3} />
          </div>
        ) : (webhooks.data?.webhooks ?? []).length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState
              title="No webhooks"
              description="Add one to receive these events in your own systems."
            />
          </div>
        ) : (
          <div className="px-5 py-4">
            <Table>
              <thead>
                <Tr>
                  <Th>URL</Th>
                  <Th>Events</Th>
                  <Th />
                </Tr>
              </thead>
              <tbody>
                {(webhooks.data?.webhooks ?? []).map((webhook) => (
                  <Tr key={webhook.id}>
                    <Td>
                      <span className="mono text-[13px] break-all">{webhook.url}</span>
                      {!webhook.active && (
                        <p className="mt-1 text-[13px] text-[var(--danger)]">
                          {webhook.disabled_reason ?? "Switched off after repeated failures."}
                        </p>
                      )}
                    </Td>
                    <Td>
                      {(webhook.events ?? []).length === 0
                        ? "Everything"
                        : (webhook.events ?? []).map(eventLabel).join(", ")}
                    </Td>
                    <Td align="right">
                      <Button
                        tone="ghost"
                        size="dense"
                        busy={removeWebhook.isPending && removeWebhook.variables?.id === webhook.id}
                        onClick={() => removeWebhook.mutate(webhook)}
                      >
                        Remove
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel
        title="What has been sent"
        description="Every attempt, with what came back. A claim that we sent something is not the same as a record of it."
        padded={false}
      >
        {deliveries.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={4} columns={4} />
          </div>
        ) : (deliveries.data?.deliveries ?? []).length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState title="Nothing sent yet" description="Deliveries appear here as they happen." />
          </div>
        ) : (
          <div className="px-5 py-4">
            <Table>
              <thead>
                <Tr>
                  <Th>Event</Th>
                  <Th>To</Th>
                  <Th>Result</Th>
                  <Th align="right">When</Th>
                </Tr>
              </thead>
              <tbody>
                {(deliveries.data?.deliveries ?? []).map((delivery) => (
                  <Tr key={delivery.id}>
                    <Td>{eventLabel(delivery.event)}</Td>
                    <Td>
                      <span className="text-[13px] text-[var(--text-secondary)] break-all">
                        {delivery.destination}
                      </span>
                      <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">
                        {delivery.channel === "email" ? "Email" : "Webhook"}
                      </p>
                    </Td>
                    <Td>
                      <Chip tone={describeDelivery(delivery).tone}>
                        {describeDelivery(delivery).label}
                      </Chip>
                      {delivery.response_detail && delivery.status !== "delivered" && (
                        <p className="mt-1 max-w-sm text-[13px] text-[var(--text-secondary)]">
                          {delivery.response_detail}
                        </p>
                      )}
                      {delivery.attempts > 1 && (
                        <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">
                          after {delivery.attempts} attempts
                        </p>
                      )}
                    </Td>
                    <Td align="right" title={absoluteTime(delivery.created_at)}>
                      {relativeTime(delivery.delivered_at ?? delivery.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
        </div>
      </Measure>
    </div>
  );
}

function AddWebhookDialog({
  orgId,
  events,
  onClose,
  onCreated,
}: {
  orgId: string;
  events: string[];
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | undefined>();

  const create = useMutation({
    mutationFn: () =>
      api.post<{ webhook: Webhook }>(`/organisations/${orgId}/webhooks`, {
        url: url.trim(),
        events: [...selected],
      }),
    onSuccess: async ({ webhook }) => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ["webhooks", orgId] });
      if (webhook.secret) onCreated(webhook.secret);
      else toast.success("Webhook added");
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.fieldErrors().url ?? err.message);
        return;
      }
      toast.error(err);
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Add a webhook"
      description="Drumlin will POST to this URL when these events happen."
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            tone="primary"
            busy={create.isPending}
            disabled={!url.trim()}
            onClick={() => create.mutate()}
          >
            Add webhook
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="URL"
          error={error}
          hint="Must be reachable from the internet over HTTPS."
          required
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={url}
              autoFocus
              placeholder="https://example.com/hooks/drumlin"
              onChange={(e) => {
                setUrl(e.target.value);
                setError(undefined);
              }}
            />
          )}
        </Field>

        {events.length > 0 && (
          <div>
            <p className="mb-2 text-[13px] font-medium text-[var(--text-primary)]">
              Events
              <span className="ml-2 font-normal text-[var(--text-muted)]">
                {selected.size === 0 ? "everything" : `${selected.size} selected`}
              </span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {events.map((event) => {
                const on = selected.has(event);
                return (
                  <button
                    key={event}
                    type="button"
                    onClick={() => {
                      const next = new Set(selected);
                      if (on) next.delete(event);
                      else next.add(event);
                      setSelected(next);
                    }}
                    className={
                      on
                        ? "rounded-full border border-transparent bg-[var(--primary-soft)] px-2.5 py-1 text-[13px] text-[var(--primary)]"
                        : "rounded-full border border-[var(--border)] px-2.5 py-1 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }
                  >
                    {eventLabel(event)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
